import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, HelperText } from 'react-native-paper';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryLegend,
  VictoryStack,
} from 'victory-native';
import PropTypes from 'prop-types';

import { scenarios } from '../../../scenarios';
import { AllScenariosDialog, ScenarioDialog } from './Scenarios';

const CROP_COLORS = ['#A6CEE3', '#3B9DDE', '#B2DF8A', '#37AC2F'];

function VerticalAlignLabel(props) {
  const { datum, scale } = props;
  const centerPos = scale.y((datum._y1 - datum._y0) / 2 + datum._y0);

  return (
    <VictoryLabel
      {...props}
      y={centerPos}
      dy={8}
      style={{
        fontSize: 11,
        fontWeight: 500,
      }}
    />
  );
}

// creates labels at the top of each stacked bar
function calculateScenarioTotals(language, results) {
  let scenarioTotals = [
    { x: language === 'en' ? 'S1' : 'E1', y: 0, label: 0 },
    { x: language === 'en' ? 'S2' : 'E2', y: 0, label: 0 },
    { x: language === 'en' ? 'S3' : 'E3', y: 0, label: 0 },
    { x: language === 'en' ? 'S4' : 'E4', y: 0, label: 0 },
    { x: language === 'en' ? 'S5' : 'E5', y: 0, label: 0 },
  ];

  // get sum of all crops for each scenario
  results.forEach((result) => {
    let scenarioIndex = scenarioTotals.findIndex(
      (scenario) => scenario.x === result[0].x
    );
    scenarioTotals[scenarioIndex].label += result[0].y;
    return;
  });

  // convert sum string with fixed decimal places (1)
  scenarioTotals = scenarioTotals.map(({ x, y, label }) => {
    return {
      x,
      y,
      label: label.toFixed(1),
    };
  });

  return scenarioTotals;
}

export default function CropMixChart({
  appStyles,
  chartName,
  language,
  pageContent,
  results,
  unit,
}) {
  const [layout, setLayout] = useState(undefined);
  const [cropBarColor, setCropBarColor] = useState({});

  const [scenario, setScenario] = useState(undefined);
  const [visible, setVisible] = useState(false);
  const [allVisible, setAllVisible] = useState(false);

  useEffect(() => {
    if (results) {
      let barColors = {};
      results.forEach((crop) => {
        if (!Object.keys(barColors).includes(crop[0].name)) {
          barColors[crop[0].name] = CROP_COLORS[Object.keys(barColors).length];
        }
      });
      setCropBarColor(barColors);
    }
  }, [results]);

  const getLegendData = () => {
    let inLegend = [];
    let legendItems = [];

    results.forEach((crop) => {
      if (!inLegend.includes(crop[0].name)) {
        inLegend.push(crop[0].name);
        legendItems.push({
          name: crop[0].name[0].toUpperCase() + crop[0].name.slice(1),
          symbol: { fill: cropBarColor[crop[0].name], type: 'square' },
        });
      }
    });

    return legendItems;
  };

  return (
    <View style={styles.container}>
      {pageContent[2] && pageContent.length > 3 ? (
        <HelperText style={appStyles.helperText}>{pageContent[2]}</HelperText>
      ) : null}
      <View
        style={{
          flex: Platform.OS !== 'web' ? 8 : 1,
          justifyContent: 'center',
        }}
        onLayout={(event) =>
          setLayout(Platform.OS !== 'web' ? event.nativeEvent.layout : null)
        }
      >
        {(results && results.length > 0 && layout !== undefined) ||
        (results && results.length > 0 && Platform.OS === 'web') ? (
          <>
            <VictoryChart
              height={475}
              domainPadding={{ x: 25, y: [0, 25] }}
              padding={{ top: 50, bottom: 75, left: 50, right: 50 }}
            >
              <ScenarioDialog
                displayUserInputs={true}
                scenario={scenario}
                setVisible={setVisible}
                visible={visible}
              />
              <AllScenariosDialog
                language={language}
                setVisible={setAllVisible}
                visible={allVisible}
              />
              <VictoryAxis
                dependentAxis
                style={sharedAxisStyles}
                label={unit ? pageContent[0] + unit : pageContent[0]}
                tickFormat={(t) => t.toFixed(2).replace(/[.,]00$/, '')}
              />
              {Platform.OS !== 'web' ? (
                <VictoryAxis style={sharedAxisStyles} />
              ) : (
                <VictoryAxis
                  style={sharedAxisStyles}
                  tickLabelComponent={
                    <VictoryLabel
                      events={{
                        onClick: (e) => {
                          // open dialog with scenario description
                          const scenarioId = parseInt(
                            e.target.textContent.split('')[1]
                          );
                          setScenario(scenarios[language][scenarioId - 1]);
                          setVisible(true);
                        },
                      }}
                      style={{
                        fill: '#555988',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    />
                  }
                />
              )}
              <VictoryStack
                style={{ data: { stroke: '#9ba49d', strokeWidth: 0.5 } }}
              >
                {results.map((data, index) => {
                  return (
                    <VictoryBar
                      key={index}
                      style={{ data: { fill: cropBarColor[data[0].name] } }}
                      data={data}
                      barRatio={0.8}
                      labels={({ datum }) =>
                        datum.y === 0
                          ? null
                          : chartName === 'waterneeds'
                          ? datum.y
                          : chartName === 'irrigationtime'
                          ? datum.y.toFixed(1)
                          : datum.y.toFixed(2)
                      }
                      labelComponent={<VerticalAlignLabel index={index} />}
                    />
                  );
                })}
                <VictoryBar
                  barRatio={0.25}
                  data={calculateScenarioTotals(language, results)}
                  labelComponent={
                    <VictoryLabel
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    />
                  }
                />
              </VictoryStack>
              <VictoryLegend
                orientation="horizontal"
                gutter={20}
                data={getLegendData()}
              />
            </VictoryChart>
            {Platform.OS !== 'web' ? (
              <Button
                icon="help-circle"
                labelStyle={{ fontSize: 24 }}
                onPress={() => setAllVisible(true)}
              />
            ) : (
              <HelperText style={{ color: '#4F4F4F', textAlign: 'center' }}>
                {pageContent[pageContent.length - 1]}
              </HelperText>
            )}
          </>
        ) : (
          <ActivityIndicator animating={true} color={'#555988'} size="large" />
        )}
      </View>
    </View>
  );
}

CropMixChart.propTypes = {
  appStyles: PropTypes.object,
  chartName: PropTypes.string,
  language: PropTypes.string,
  pageContent: PropTypes.array,
  results: PropTypes.array,
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' && { justifyContent: 'center' }),
    ...(Platform.OS === 'web' && { alignItems: 'center' }),
  },
});

const sharedAxisStyles = {
  tickLabels: {
    fontSize: 11,
  },
  axisLabel: {
    padding: 38,
    fontSize: 13,
  },
};
